import { Session, AuthError } from '@supabase/supabase-js';
import { supabase, SUPABASE_AUTH_STORAGE_KEY } from '../supabase/client';
import { authBackoff } from '../utils/exponential-backoff';
import { logger } from '../utils/logger';
import { storageBackup } from '../supabase/storage-backup';

export class TokenManager {
  private refreshQueue: Promise<Session> | null = null;
  private lastRefreshError: AuthError | null = null;
  private failedRefreshCount = 0;
  
  async refreshToken(): Promise<Session> {
    if (this.refreshQueue) {
      logger.debug('Token refresh already in progress, joining queue');
      return this.refreshQueue;
    }
    
    this.refreshQueue = this.performRefresh()
      .finally(() => {
        this.refreshQueue = null;
      });
    
    return this.refreshQueue;
  }
  
  private async performRefresh(): Promise<Session> {
    try {
      logger.debug('Starting token refresh with exponential backoff');
      
      const session = await authBackoff.execute(
        async () => {
          const { data, error } = await supabase.auth.refreshSession();
          
          if (error) {
            if (this.isInvalidTokenError(error)) {
              logger.error('Invalid refresh token detected', { error });
              throw new InvalidTokenError(error.message);
            }
            
            throw error;
          }
          
          if (!data.session) {
            throw new Error('No session returned from refresh');
          }
          
          return data.session;
        },
        (error) => {
          if (error instanceof InvalidTokenError) {
            return false;
          }
          
          if (this.failedRefreshCount >= 3) {
            logger.warn('Too many refresh failures, stopping retries');
            return false;
          }
          
          const shouldRetry = this.isRetriableError(error);
          if (shouldRetry) {
            this.failedRefreshCount++;
            logger.debug('Retrying token refresh', { 
              attempt: this.failedRefreshCount,
              error: error.message 
            });
          }
          
          return shouldRetry;
        }
      );
      
      this.failedRefreshCount = 0;
      this.lastRefreshError = null;
      logger.info('Token refresh successful');
      
      return session;
      
    } catch (error) {
      logger.error('Token refresh failed after retries', { error });
      this.lastRefreshError = error as AuthError;
      
      if (error instanceof InvalidTokenError) {
        logger.warn('Invalid token detected, clearing session');
        await this.clearInvalidSession();
      }
      
      throw error;
    }
  }
  
  private isInvalidTokenError(error: AuthError): boolean {
    return (
      error.message?.includes('refresh_token_not_found') ||
      error.message?.includes('invalid refresh token') ||
      error.message?.includes('Token has expired') ||
      error.status === 400 ||
      error.status === 401
    );
  }
  
  private isRetriableError(error: any): boolean {
    if (error.name === 'NetworkError' || error.message?.includes('network')) {
      return true;
    }
    
    if (error.status >= 500 && error.status < 600) {
      return true;
    }
    
    if (error.status === 429) {
      return true;
    }
    
    if (error.message?.includes('timeout')) {
      return true;
    }
    
    return false;
  }
  
  private async clearInvalidSession(): Promise<void> {
    try {
      await supabase.auth.signOut();
      
      if (typeof window !== 'undefined') {
        localStorage.removeItem(SUPABASE_AUTH_STORAGE_KEY);
        sessionStorage.removeItem(SUPABASE_AUTH_STORAGE_KEY);
        await storageBackup.clear(SUPABASE_AUTH_STORAGE_KEY);
      }
    } catch (error) {
      logger.error('Failed to clear invalid session', { error });
    }
  }
  
  getLastError(): AuthError | null {
    return this.lastRefreshError;
  }
  
  reset(): void {
    this.refreshQueue = null;
    this.lastRefreshError = null;
    this.failedRefreshCount = 0;
  }
}

class InvalidTokenError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidTokenError';
  }
}

export const tokenManager = new TokenManager();
